import $ from 'jquery'

import type { SfJQuery } from '@app/types'

import { reinitReportTables } from '@app/application/report/behaviors/report-table'
// For its side effect: this is what defines $.fn.initializeDom, which the swap
// below runs on the new table.
import '@app/setup/on_dom_load'

// Where reports/show.html.slim renders the table. On the first load that is the
// lazily-loading turbo frame; once `render=1` is in the URL reports#show renders
// `_table_content` straight onto the page and there is no frame at all, so the
// wrapper carries a hook of its own.
//
// The frame is swapped from the inside rather than replaced, or it would stop
// reloading itself (a period set change, a finished async render).
const FRAME_ID = 'report-table'
const WRAPPER_SELECTOR = '[data-report-table-content]'

const findTableContent = (): HTMLElement | null =>
  document.getElementById(FRAME_ID) ??
  document.querySelector<HTMLElement>(WRAPPER_SELECTOR)

// Puts a server-rendered `reports/_table_content` in place of the one on screen.
//
// The behaviours bound to a report table belong to the element that was there,
// and the rows the user collapsed or expanded are held in local storage rather
// than in the markup — so both are re-established against the new table, exactly
// as they are when the frame reloads on its own.
export const swapReportTable = (html: string): void => {
  const content = findTableContent()
  if (!content) throw new Error('No report table to replace')

  // Parsed rather than assigned through innerHTML: the markup is our own
  // server-rendered `reports/_table_content`, but a raw innerHTML sink reads as
  // an XSS vector to any scanner looking at this file, and parsing first is what
  // the app already does elsewhere for server HTML (see the admin queue progress
  // controller). Same result, nothing to take on trust.
  const parsed = new DOMParser().parseFromString(html, 'text/html')
  content.replaceChildren(...parsed.body.childNodes)

  // The app's DOM initialization runs on turbolinks:load and turbo:frame-load,
  // neither of which a manual swap fires — so without this the styled tooltips
  // inside the new table are never built (and anything else registered through
  // onDomLoad goes with them). Scoped to the swapped content rather than the
  // document, or every Vue-controlled element on the page would remount.
  ;($(content) as SfJQuery).initializeDom?.()

  // Not an onDomLoad handler, so initializeDom above does not cover it: the
  // report table binds on turbolinks:load / turbo:frame-load instead.
  reinitReportTables()
}
